import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Plus, Search, Image as ImageIcon, X } from "lucide-react";

const STATUSES = ["All", "Pending", "Assigned", "In Progress", "Ready", "Delivered"];
const CATEGORIES = ["All", "Necklace", "Ring", "Earring", "Bangle", "Bracelet",
  "Pendant", "Chain", "Mangalsutra", "Nose Pin", "Anklet", "Kadli", "Other"];

export default function OrdersList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);

  const status = searchParams.get("status") || "All";
  const jtype = searchParams.get("jewellery_type") || "All";
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const q = searchParams.get("q") || "";

  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (!val || val === "All") next.delete(key);
    else next.set(key, val);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "All") params.status = status;
    if (jtype !== "All") params.jewellery_type = jtype;
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
    if (dateTo) {
      // include the entire end day
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      params.date_to = end.toISOString();
    }
    const { data } = await api.get("/orders", { params });
    setItems(data);
  }, [q, status, jtype, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => setSearchParams({}, { replace: true });
  const hasFilters = status !== "All" || jtype !== "All" || dateFrom || dateTo || q;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Workflow</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Orders</h1>
        </div>
        <Link to="/orders/new">
          <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="new-order-button">
            <Plus className="w-4 h-4 mr-1.5" /> New Order
          </Button>
        </Link>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-[1fr_180px_180px_160px_160px_auto] gap-3 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
            <Input
              placeholder="Search by order # or title…"
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              className="pl-9"
              data-testid="order-search-input"
            />
          </div>
          <Select value={status} onValueChange={(v) => setParam("status", v)}>
            <SelectTrigger data-testid="order-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={jtype} onValueChange={(v) => setParam("jewellery_type", v)}>
            <SelectTrigger data-testid="order-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((s) => <SelectItem key={s} value={s}>{s === "All" ? "All Categories" : s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setParam("date_from", e.target.value)}
            data-testid="order-date-from"
            placeholder="From"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setParam("date_to", e.target.value)}
            data-testid="order-date-to"
            placeholder="To"
          />
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>
        <div className="text-xs text-zinc-500 mt-3 num">{items.length} order(s)</div>
      </Card>

      <Card className="overflow-hidden p-0">
        {items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No orders found.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="orders-list">
            {items.map((o) => {
              const thumb = o.image_paths?.[0];
              return (
                <Link
                  key={o.id}
                  to={`/orders/${o.id}`}
                  className="grid grid-cols-[56px_1fr_auto] gap-3 p-4 items-center hover:bg-zinc-50"
                >
                  <div className="w-14 h-14 rounded-md bg-zinc-100 border border-border overflow-hidden grid place-items-center">
                    {thumb ? (
                      <img src={fileUrl(thumb)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-zinc-500 break-all">{o.order_no}</span>
                      <StatusBadge status={o.status} />
                      <PriorityBadge priority={o.priority} />
                      {o.jewellery_type && (
                        <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full">
                          {o.jewellery_type}
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-sm truncate mt-1">{o.title}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {o.customer_name || "—"}{o.karigar_name ? ` · ${o.karigar_name}` : ""}
                    </div>
                  </div>
                  <div className="text-right text-xs text-zinc-500">
                    {o.expected_delivery
                      ? <>Due {new Date(o.expected_delivery).toLocaleDateString()}</>
                      : new Date(o.created_at).toLocaleDateString()}
                    {o.final_weight > 0 && (
                      <div className="num text-zinc-700 mt-0.5">{o.final_weight.toFixed(2)} g</div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
